/**
 * Certifications & Achievements — admin catalog, per-user grants, unlock notifications.
 */

import {
  addDoc,
  arrayUnion,
  collection,
  doc,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { db, storage } from './firebase';
import type { EmployeeAwardDefinition, EmployeeAwardGrant } from '../types';
import { buildGrantPayloadFromDefinition } from './employeeAchievementsLogic';

export {
  buildGrantPayloadFromDefinition,
  pickNextUnseenAwardGrant,
  shouldShowAwardGrant,
} from './employeeAchievementsLogic';

const DEFINITIONS_COLLECTION = 'employeeAwardDefinitions';
const GRANTS_COLLECTION = 'employeeAwardGrants';

function parseDefinition(id: string, data: Record<string, unknown>): EmployeeAwardDefinition {
  const createdAtRaw = data.createdAt;
  const updatedAtRaw = data.updatedAt;
  return {
    id,
    name: typeof data.name === 'string' ? data.name : '',
    description: typeof data.description === 'string' ? data.description : '',
    iconUrl: typeof data.iconUrl === 'string' ? data.iconUrl : '',
    iconStoragePath: typeof data.iconStoragePath === 'string' ? data.iconStoragePath : '',
    isActive: data.isActive !== false,
    createdAt: createdAtRaw instanceof Timestamp ? createdAtRaw.toDate() : new Date(0),
    updatedAt: updatedAtRaw instanceof Timestamp ? updatedAtRaw.toDate() : new Date(0),
    createdBy: typeof data.createdBy === 'string' ? data.createdBy : '',
  };
}

function parseGrant(id: string, data: Record<string, unknown>): EmployeeAwardGrant {
  const grantedAtRaw = data.grantedAt;
  return {
    id,
    userId: typeof data.userId === 'string' ? data.userId : '',
    awardDefinitionId: typeof data.awardDefinitionId === 'string' ? data.awardDefinitionId : '',
    awardName: typeof data.awardName === 'string' ? data.awardName : '',
    awardDescription: typeof data.awardDescription === 'string' ? data.awardDescription : '',
    iconUrl: typeof data.iconUrl === 'string' ? data.iconUrl : '',
    grantedAt: grantedAtRaw instanceof Timestamp ? grantedAtRaw.toDate() : new Date(0),
    grantedBy: typeof data.grantedBy === 'string' ? data.grantedBy : '',
    ...(typeof data.grantNote === 'string' && data.grantNote.trim()
      ? { grantNote: data.grantNote.trim() }
      : {}),
  };
}

function sortGrants(grants: EmployeeAwardGrant[], direction: 'asc' | 'desc'): EmployeeAwardGrant[] {
  return [...grants].sort((a, b) => {
    const diff = a.grantedAt.getTime() - b.grantedAt.getTime();
    return direction === 'asc' ? diff : -diff;
  });
}

function subscribeToUserAwardGrantsQuery(
  userId: string,
  callback: (grants: EmployeeAwardGrant[]) => void,
  direction: 'asc' | 'desc',
): () => void {
  // Single-field equality query only — sort client-side so a composite index isn't required.
  const q = query(collection(db, GRANTS_COLLECTION), where('userId', '==', userId));
  return onSnapshot(
    q,
    snapshot => {
      const grants = snapshot.docs.map(d => parseGrant(d.id, d.data() as Record<string, unknown>));
      callback(sortGrants(grants, direction));
    },
    error => {
      console.warn('employeeAwardGrants subscription failed:', error);
      callback([]);
    },
  );
}

export function subscribeToAwardDefinitions(
  callback: (definitions: EmployeeAwardDefinition[]) => void,
): () => void {
  const q = query(collection(db, DEFINITIONS_COLLECTION), orderBy('name', 'asc'));
  return onSnapshot(
    q,
    snapshot => {
      callback(
        snapshot.docs.map(d => parseDefinition(d.id, d.data() as Record<string, unknown>)),
      );
    },
    error => {
      console.warn('employeeAwardDefinitions subscription failed:', error);
      callback([]);
    },
  );
}

export function subscribeToUserAwardGrants(
  userId: string,
  callback: (grants: EmployeeAwardGrant[]) => void,
): () => void {
  return subscribeToUserAwardGrantsQuery(userId, callback, 'desc');
}

/** Ascending order for notification queue (oldest unseen first). */
export function subscribeToUserAwardGrantsAsc(
  userId: string,
  callback: (grants: EmployeeAwardGrant[]) => void,
): () => void {
  return subscribeToUserAwardGrantsQuery(userId, callback, 'asc');
}

export async function uploadAwardIconPng(
  file: File,
  definitionId: string,
): Promise<{ iconUrl: string; iconStoragePath: string }> {
  if (file.type !== 'image/png') {
    throw new Error('Award icons must be PNG files.');
  }
  if (file.size > 2 * 1024 * 1024) {
    throw new Error('Award icon must be 2 MB or smaller.');
  }
  const storagePath = `media-library/achievements/${definitionId}-${Date.now()}.png`;
  const storageRef = ref(storage, storagePath);
  await uploadBytes(storageRef, file, { contentType: 'image/png' });
  const iconUrl = await getDownloadURL(storageRef);
  return { iconUrl, iconStoragePath: storagePath };
}

export async function createAwardDefinition(input: {
  name: string;
  description: string;
  iconUrl: string;
  iconStoragePath: string;
  createdBy: string;
  definitionId?: string;
}): Promise<string> {
  const name = input.name.trim();
  const description = input.description.trim();
  if (!name) throw new Error('Award name is required.');
  if (!input.iconUrl) throw new Error('Award icon is required.');

  const definitionRef = input.definitionId
    ? doc(db, DEFINITIONS_COLLECTION, input.definitionId)
    : doc(collection(db, DEFINITIONS_COLLECTION));

  await setDoc(definitionRef, {
    name,
    description,
    iconUrl: input.iconUrl,
    iconStoragePath: input.iconStoragePath,
    isActive: true,
    createdBy: input.createdBy,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return definitionRef.id;
}

export async function updateAwardDefinition(
  definitionId: string,
  patch: Partial<Pick<EmployeeAwardDefinition, 'name' | 'description' | 'iconUrl' | 'iconStoragePath' | 'isActive'>>,
): Promise<void> {
  await updateDoc(doc(db, DEFINITIONS_COLLECTION, definitionId), {
    updatedAt: serverTimestamp(),
    ...(typeof patch.name === 'string' ? { name: patch.name.trim() } : {}),
    ...(typeof patch.description === 'string' ? { description: patch.description.trim() } : {}),
    ...(typeof patch.iconUrl === 'string' ? { iconUrl: patch.iconUrl } : {}),
    ...(typeof patch.iconStoragePath === 'string' ? { iconStoragePath: patch.iconStoragePath } : {}),
    ...(typeof patch.isActive === 'boolean' ? { isActive: patch.isActive } : {}),
  });
}

export async function grantEmployeeAward(input: {
  userId: string;
  awardDefinitionId: string;
  grantedBy: string;
  grantNote?: string;
}): Promise<string> {
  const defSnap = await getDoc(doc(db, DEFINITIONS_COLLECTION, input.awardDefinitionId));
  if (!defSnap.exists()) {
    throw new Error('Award definition not found.');
  }
  const definition = parseDefinition(defSnap.id, defSnap.data() as Record<string, unknown>);
  if (!definition.isActive) {
    throw new Error('This award is inactive and cannot be granted.');
  }
  if (!definition.id) {
    throw new Error('Award definition is invalid.');
  }

  const payload = buildGrantPayloadFromDefinition(
    definition,
    input.userId,
    input.grantedBy,
    input.grantNote,
  );

  const grantRef = await addDoc(collection(db, GRANTS_COLLECTION), {
    ...payload,
    grantedAt: serverTimestamp(),
  });
  return grantRef.id;
}

export async function markAwardGrantSeen(userId: string, grantId: string): Promise<void> {
  await updateDoc(doc(db, 'users', userId), {
    seenAwardGrantIds: arrayUnion(grantId),
  });
}
